import { Component, EventEmitter, HostListener, Inject, OnInit, OnDestroy, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { CustomerFacade, LoginFacade, NotificationFacade, ApplicationFacade } from '@ridango/components';
import Env from '@ridango/core/env';
import { AccountDeleteConfirmationType } from '@ridango/core/lib/enums/features.enum';
import { Observable } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { CustomerAccountDeleteDialogComponent } from '../customer-account-delete-dialog/customer-account-delete-dialog.component';
import { AuthService, BaseComponent, SetCustomerProfileInformation } from '@ridango/core';
import { ModalController } from '@ionic/angular';
import { phoneValidNumberValidator } from '@ridango/core/lib/validators/phone.validator';
import { PermissionsFacade } from '@ridango/components/lib/permissions';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { PhoneNumberUtil } from 'google-libphonenumber';

const phoneNumberUtil = PhoneNumberUtil.getInstance();

@Component({
    selector: 'rwidget-customer-account-detail-stw',
    templateUrl: './customer-account-detail-stw.component.html',
    styleUrls: ['./customer-account-detail-stw.component.scss']
})
export class CustomerAccountDetailSTWComponent extends BaseComponent implements OnInit, OnDestroy {
    profileUpdateOptions = this.env.PROFILE_UPDATE;
    profile$: Observable<any> = this.customerFacade.profile$;
    actions$: Observable<string[]> = this.customerFacade.profileActions$;
    customerFields$: Observable<string[]> = this.customerFacade.customerFields$;

    isDeletionSuccess$: Observable<boolean> = this.customerFacade.isDeletionSuccess$;
    error$: Observable<any> = this.customerFacade.error$;

    locale = 'en';
    contactPhone: string;

    accountDeleteConfirmationType = AccountDeleteConfirmationType;
    accountDeletionConfirmed = false;

    canEdit = false;

    _customerForm: FormGroup;

    @Output() didEditSuccess = new EventEmitter<any>();
    accountPersonalizationState = this.authService.getAccountPersonalizationState();
    shouldShowPersonalizationForAccount = this.env?.ACCOUNT?.PERSONALIZATION_ENABLED;
    assetsPath$ = this.applicationFacade.assetsPath$;
    isAuthenticatedCustomer = false;

    private readonly LOG_OUT = 'Log out';

    constructor(
        public permissionsFacade: PermissionsFacade,
        @Inject('ENV') public env: Env,
        private customerFacade: CustomerFacade,
        private router: Router,
        private route: ActivatedRoute,
        private activatedRoute: ActivatedRoute,
        private translate: TranslateService,
        private notification: NotificationFacade,
        private loginFacade: LoginFacade,
        private readonly authService: AuthService,
        private applicationFacade: ApplicationFacade,
        private modalCtrl: ModalController,
        private analyticsService: AnalyticsService
    ) {
        super();
    }

    ngOnInit() {
        this._customerForm = new FormGroup({
            // Name and phone are legacy fields and should be removed after all application use new WMB
            name: new FormControl(''),
            phone: new FormControl('', [phoneValidNumberValidator]),
            email: new FormControl({ value: '', disabled: true }),
            firstName: new FormControl(
                '',
                this.profileUpdateOptions?.FIRST_NAME_MANDATORY ? [Validators.required] : []
            ),
            lastName: new FormControl('', this.profileUpdateOptions?.LAST_NAME_MANDATORY ? [Validators.required] : []),
            phoneNumber: new FormControl(
                this.env?.REGISTRATION?.USE_COUNTRY_CODE_ON_FORMS ? this.env?.DEFAULT_PHONE_COUNTRY_CODE ?? '' : '',
                this.profileUpdateOptions?.PHONE_NUMBER_MANDATORY
                    ? [Validators.required, phoneValidNumberValidator]
                    : [phoneValidNumberValidator]
            )
        });

        this.customerFacade.getCustomerProfile();
        this.customerFacade.profile$.pipe(takeUntil(this.componentDestroyed$)).subscribe((d) => {
            this.authService.setAccountPersonalizationState(d.authenticated);
            this.isAuthenticatedCustomer = d.authenticated;
            this._customerForm.patchValue(d);
            const formattedPhone = d.phoneNumber ? `+${d.countryCode}${d.phoneNumber}` : null;
            const phone = this.getPhoneFormValue(d.phone);
            this._customerForm.patchValue({
                firstName: d.firstName,
                lastName: d.lastName,
                phone,
                phoneNumber: formattedPhone
            });
        });

        this.initializeQuerySubscription();

        this.customerFacade.isFormSubmitted$.pipe(takeUntil(this.componentDestroyed$)).subscribe((isSubmitted) => {
            if (isSubmitted) {
                this.customerFacade.error$.pipe(take(1)).subscribe((response) => {
                    if (response?.error) {
                        this.scrollToTop();
                        this.handleError(response.error);
                    } else {
                        this.notification.success(this.translate.instant('LOCAL_PROFILE_SAVED_SUCCESS'), {
                            title: this.translate.instant('LOCAL_NOTIFICATION_HEADING_SUCCESS'),
                            showAfterRouteChange: false
                        });
                        this.didEditSuccess.emit();
                        this.scrollToTop();
                        this.canEdit = false;
                    }
                });
            }
        });
    }

    private getPhoneFormValue(phone: string | { code?: string; number?: string | number } | null | undefined): string {
        if (typeof phone === 'string') {
            return phone;
        }

        if (phone?.code && phone?.number != null) {
            return `${phone.code}${phone.number}`;
        }

        return '';
    }

    toggleAccountEdit() {
        this.canEdit = !this.canEdit;
        this.scrollToTop();
    }

    initializeQuerySubscription(): void {
        this.route.queryParams.pipe(takeUntil(this.componentDestroyed$)).subscribe((params) => {
            const personalizationParam = params['personalization'];

            switch (personalizationParam) {
                case 'ok':
                    this.notification.success(this.translate.instant('ACCOUNT_PERSONALIZATION_SUCCESSFUL'), {
                        title: this.translate.instant('LOCAL_NOTIFICATION_HEADING_SUCCESS'),
                        showAfterRouteChange: false
                    });
                    this.authService.setAccountPersonalizationState(true);
                    break;
                case 'error':
                    this.notification.error(this.translate.instant('SOMETHING_WENT_WRONG'));
                    this.authService.setAccountPersonalizationState(false);
                    break;
                case 'error-duplicate':
                    this.notification.error(this.translate.instant('ERROR_DUPLICATE'));
                    this.authService.setAccountPersonalizationState(false);
                    break;
                default:
                    break;
            }
            this.accountPersonalizationState = this.authService.getAccountPersonalizationState();
        });
    }

    onSubmit() {
        if (this._customerForm.valid) {
            if (this.env?.IS_NEW_AFC && this.env?.WMB) {
                const phoneInput = (this._customerForm.value.phoneNumber ?? '').toString().trim();
                if (!phoneInput) {
                    this.customerFacade.saveCustomerProfile({
                        ...this._customerForm.value,
                        phoneNumber: null,
                        phone: null,
                        lang: this.translate.currentLang
                    } as SetCustomerProfileInformation);
                    return;
                } else {
                    const phoneNumber = phoneNumberUtil.parse(phoneInput);
                    this.customerFacade.saveCustomerProfile({
                        ...this._customerForm.value,
                        phoneNumber: phoneNumber.getNationalNumber(),
                        phone: {
                            code: `+${phoneNumber.getCountryCode()}`,
                            number: phoneNumber.getNationalNumber()
                        },
                        lang: this.translate.currentLang
                    } as SetCustomerProfileInformation);
                }
            } else {
                const legacyPayload: any = {
                    ...this._customerForm.value,
                    lang: this.translate.currentLang
                };
                delete legacyPayload.phoneNumber;
                delete legacyPayload.firstName;
                delete legacyPayload.lastName;
                this.customerFacade.saveCustomerProfile(legacyPayload as SetCustomerProfileInformation);
            }
        }
    }

    // This will currently navigate the user to the home page on Mobile Apps
    handleBackClicked() {
        if (this.env.MOBILE_MENU_CUSTOMIZATION?.IS_PAGE) {
            window.history.back();
        } else {
            this.router.navigate([this.translate.currentLang]);
        }
    }

    /**
     * Ensures that when a form field receives focus on mobile devices,
     * it scrolls into view to avoid being obscured by the virtual keyboard.
     * @param event FocusEvent triggered when an element gains focus.
     */
    @HostListener('focusin', ['$event'])
    handleFocusIn(event: FocusEvent) {
        const target = event.target as HTMLElement;
        const tagName = target?.tagName?.toLowerCase();
        if (!tagName || !['input', 'textarea', 'select'].includes(tagName)) {
            return;
        }

        // Keep focused fields visible when the mobile keyboard opens.
        requestAnimationFrame(() => {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }

    actionClicked(action: string, event) {
        event.stopPropagation();
        event.preventDefault();

        switch (action) {
            case 'ACCOUNT_ACTION_EDIT':
                this.toggleAccountEdit();
                break;
            case 'ACCOUNT_ACTION_CHANGE_PASSWORD':
                this.navigateRelative(['change-password']);
                break;
            case 'ACCOUNT_ACTION_CHANGE_EMAIL':
                this.navigateRelative(['change-email']);
                break;
            case 'ACCOUNT_ACTION_DELETE_ACCOUNT':
                this.deleteConfirmation();
                break;
        }
    }

    async deleteConfirmation() {
        const modal = await this.modalCtrl.create({
            component: CustomerAccountDeleteDialogComponent,
            cssClass: 'delete-account-modal',
            componentProps: {}
        });

        await modal.present();

        modal.onDidDismiss().then(async ({ data }) => {
            if (data) {
                if (this.env.ACCOUNT_DELETE_CONFIRM_TYPE === AccountDeleteConfirmationType.CONFIRM_WITH_EMAIL) {
                    this.customerFacade.sendCustomerProfileDeletionEmail();
                } else if (
                    this.env.ACCOUNT_DELETE_CONFIRM_TYPE === AccountDeleteConfirmationType.CONFIRM_WITH_TICKBOX
                ) {
                    // Start deletion process
                    this.customerFacade.deleteProfile();

                    // Wait for deletion to complete, then show notification and logout
                    this.customerFacade.isDeletionSuccess$
                        .pipe(
                            filter((success) => success === true),
                            take(1)
                        )
                        .subscribe(() => {
                            this.notification.success(this.translate.instant('LOCAL_CUSTOMER_FORGET_SUCCESS'), {
                                title: this.translate.instant('LOCAL_CUSTOMER_FORGET_SUCCESS_TITLE'),
                                showAfterRouteChange: true
                            });

                            this.loginFacade.logout();
                            this.router.navigate([this.translate.currentLang, 'home']);
                            this.customerFacade.clearCustomerProfile();
                        });
                }
            }
        });
    }

    findProperty(profile, code: string) {
        const property = profile.customer_properties.find((object) => object.code === code);
        if (property) {
            return property;
        } else {
            return 0;
        }
    }

    navigateRelative(to: any[]) {
        this.router.navigate(to, { relativeTo: this.activatedRoute });
    }

    handleError(e: any) {
        if (e.message) {
            this.notification.error(this.translate.instant(e.message));
        } else {
            this.notification.error(this.translate.instant(e));
        }
    }

    getPhoneNumber(profile): string | undefined {
        if (profile.customer_properties) {
            this.contactPhone = this.findProperty(profile, 'CONTACT_PHONE').value;
            return this.contactPhone;
        }
    }

    logout() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.PROFILE,
            name: this.LOG_OUT
        });
        this.loginFacade.logout();
        this.notification.success(this.translate.instant('LOCAL_LOGOUT_SUCCESS'), { showAfterRouteChange: true });
        this.permissionsFacade.logoutSuccess();
        this.router.navigate([this.translate.currentLang, 'login']);
    }

    public async handlePersonalizeAccount() {
        await this.router.navigate([this.translate.currentLang, 'account', 'personalize']);
    }

    private scrollToTop(): void {
        document.querySelector('ion-content')?.scrollToTop(300);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
